import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/useAppStore';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { cn } from '../../lib/utils';
import { 
  Plus,
  X,
  Loader2,
  FlaskConical,
  ExternalLink,
  MessageCircle,
  Save,
  PanelLeft,
  ArrowLeftCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { userDataApi, UserChat } from '../../services/data_apis';
import { Card } from '../ui/card';

interface SidebarProps {
  isMobile?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ isMobile = false }) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { 
    sidebarCollapsed, 
    setSidebarCollapsed,
    setMobileSidebarOpen,
    templates
  } = useAppStore();
  
  // Template loading states are kept for future template sidebar feature
  // const [loadingTemplates, setLoadingTemplates] = useState(false);
  // const [templatesError, setTemplatesError] = useState<string | null>(null);
  // const [recentTemplates, setRecentTemplates] = useState<UserTemplate[]>([]);
  
  const [loadingResearch, setLoadingResearch] = useState(false);
  const [researchError, setResearchError] = useState<string | null>(null);
  const [recentResearch, setRecentResearch] = useState<UserChat[]>([]);

  // Load data on mount
  useEffect(() => {
    if (isAuthenticated) {
      // loadTemplates(); // Uncomment when template sidebar feature is implemented
      loadRecentResearch();
    }
  }, [isAuthenticated]);

  // Template loading function - kept for future use
  // const loadTemplates = async () => {
  //   if (!isAuthenticated) return;
    
  //   try {
  //     setLoadingTemplates(true);
  //     setTemplatesError(null);
  //     const token = await getAccessTokenSilently();
  //     const response = await userDataApi.getUserTemplates(5, 0, token);
  //     setRecentTemplates(response.templates);
  //     setTemplates(response.templates);
  //   } catch (err) {
  //     console.error('Failed to load templates:', err);
  //     setTemplatesError('Failed to load templates');
  //   } finally {
  //     setLoadingTemplates(false);
  //   }
  // };

  const loadRecentResearch = async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoadingResearch(true);
      setResearchError(null);
      const token = await getAccessTokenSilently();
      const response = await userDataApi.getUserChats(50, 0, 'completed', token);
      setRecentResearch(response.chats.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ));
    } catch (err) {
      console.error('Failed to load research history:', err);
      setResearchError('Failed to load research history');
    } finally {
      setLoadingResearch(false);
    }
  };

  // Template click handler - kept for future use
  // const handleTemplateClick = (template: UserTemplate) => {
  //   navigate('/research', {
  //     state: {
  //       query: template.initial_query,
  //       researchLength: template.research_length,
  //       templateId: template.id,
  //       templateName: template.template_name
  //     }
  //   });
  //   if (isMobile) {
  //     setMobileSidebarOpen(false);
  //   }
  // };

  const handleResearchClick = (research: UserChat) => {
    // Navigate directly to research page to load existing session data
    navigate('/research', {
      state: {
        sessionId: research.id,
        query: research.initial_query,
        researchLength: research.research_length,
        resumeSession: true
      }
    });
    if (isMobile) {
      setMobileSidebarOpen(false);
    }
  };

  const navItems = [
    {
      title: 'New Research',
      icon: Plus,
      path: '/home',
      badge: null,
    },
    {
      title: 'Research History',
      icon: MessageCircle,
      path: '/research-history',
      badge: null,
    },
    {
      title: 'Templates',
      icon: Save,
      path: '/templates',
      badge: templates.length > 0 ? templates.length.toString() : null,
    },
  ];

  const getResearchLengthColor = (length: string) => {
    switch (length) {
      case 'quick_scan': return 'bg-green-100 text-green-800';
      case 'targeted': return 'bg-blue-100 text-blue-800';
      case 'deep_dive': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <motion.aside
      animate={{ width: sidebarCollapsed && !isMobile ? 80 : 280 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "relative flex h-full flex-col border-r border-gray-200 bg-white",
        isMobile && "w-[280px]"
      )}
    >
      {/* Sidebar Header */}
      <div className="border-b border-gray-200">
        {/* Logo Section */}
        <div className="flex h-16 items-center justify-center px-4">
          {(!sidebarCollapsed || isMobile) && (
            <div 
              className="cursor-pointer"
              onClick={() => {
                navigate('/');
                if (isMobile) {
                  setMobileSidebarOpen(false);
                }
              }}
            >
              <img 
                src="/images/shodha-logo.png" 
                alt="Shodha Logo" 
                className="h-12 w-auto object-contain"
              />
            </div>
          )}
          
          {(sidebarCollapsed && !isMobile) && (
            <div 
              className="cursor-pointer"
              onClick={() => {
                navigate('/');
              }}
            >
              <img 
                src="/images/favicon.png" 
                alt="Shodha Logo" 
                className="h-16 w-16 object-contain"
              />
            </div>
          )}
          
          {/* Close button - Mobile only */}
          {isMobile && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileSidebarOpen(false)}
              className="h-8 w-8 p-0 ml-auto"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {/* Collapse button - Desktop only - Below logo */}
        {!isMobile && (
          <div className="flex justify-center pb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="h-6 w-6 p-0"
            >
              {sidebarCollapsed ? (
                <PanelLeft className="h-4 w-4" />
              ) : (
                <ArrowLeftCircle className="h-4 w-4" />
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Main scrollable area: nav + recent research */}
      <div className="flex-1 overflow-hidden flex flex-col">
      {/* Navigation */}
      <nav className="space-y-1 p-2">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                "hover:bg-gray-100",
                isActive
                  ? "bg-gradient-to-r from-blue-50 to-orange-50 text-blue-600"
                  : "text-gray-700",
                sidebarCollapsed && !isMobile && "justify-center"
              )
            }
            onClick={() => isMobile && setMobileSidebarOpen(false)}
          >
            <item.icon className="h-5 w-5 shrink-0" />
            {(!sidebarCollapsed || isMobile) && (
              <>
                <span className="flex-1">{item.title}</span>
                {item.badge && (
                  <Badge variant="secondary" className="ml-auto">
                    {item.badge}
                  </Badge>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Recent Research Section */}
      {(!sidebarCollapsed || isMobile) && (
        <>
          <Separator className="mx-4" />
          <div className="p-2 flex flex-col min-h-0">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">
                Recent Research
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/home')}
                className="h-7 px-2 text-xs"
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto hide-scrollbar">
              {loadingResearch && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              )}
              
              {!loadingResearch && researchError && (
                <div className="text-center py-4">
                  <p className="text-xs text-red-500">{researchError}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={loadRecentResearch}
                    className="mt-2 text-xs"
                  >
                    Retry
                  </Button>
                </div>
              )}
              
              {!loadingResearch && !researchError && recentResearch.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-xs text-gray-500">
                    No research yet
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/home')}
                    className="mt-2 text-xs"
                  >
                    Start First Research
                  </Button>
                </div>
              )}
              
              {!loadingResearch && !researchError && recentResearch.map((research) => (
                <Card
                  key={research.id}
                  className="mb-2 cursor-pointer p-3 hover:bg-gray-50"
                  onClick={() => handleResearchClick(research)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h4 className="truncate text-sm font-medium text-gray-900">
                        {research.title || research.initial_query}
                      </h4>
                      <p className="mt-1 line-clamp-2 text-xs text-gray-600">
                        {research.initial_query}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResearchClick(research);
                      }}
                    >
                      <FlaskConical className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Badge 
                      variant="outline" 
                      className={cn("text-xs", getResearchLengthColor(research.research_length))}
                    >
                      {research.research_length.replace('_', ' ')}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      {formatDate(research.created_at)}
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </>
      )}
      </div>

      {/* Bottom Actions */}
      <div className="border-t border-gray-200 p-2">
        {/* Know More Features Link */}
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-start gap-3 text-sm mt-1",
            sidebarCollapsed && !isMobile && "justify-center"
          )}
          onClick={() => window.open('https://www.shodha.ai', '_blank')}
        >
          <ExternalLink className="h-5 w-5" />
          {(!sidebarCollapsed || isMobile) && "Know more features"}
        </Button>
      </div>
    </motion.aside>
  );
};